import { useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, KeyRound } from "lucide-react";
import { AuthCardLayout } from "@/components/layout/AuthCardLayout";
import { Button } from "@/components/ui/button";
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { resetPassword } from "@/lib/api/auth";
import { useDialog } from "@/lib/dialogs/DialogProvider";
import { useAsyncAction } from "@/lib/hooks/useAsyncAction";

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const { alert } = useDialog();
  const { run, pending } = useAsyncAction();
  const navigate = useNavigate();

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    run(async () => {
      if (password !== confirmation) throw new Error("Die Passwörter stimmen nicht überein.");
      await resetPassword(token, password);
      await alert("Das Passwort wurde geändert. Du kannst dich jetzt anmelden.", { title: "Passwort geändert" });
      navigate("/login", { replace: true });
    });
  };

  if (!token) {
    return (
      <AuthCardLayout>
        <h3 className="mb-2 flex items-center justify-center gap-2 text-lg font-bold text-primary">
          <AlertTriangle className="size-5" aria-hidden="true" /> Kein gültiger Link
        </h3>
        <p className="text-sm leading-[1.45] text-muted-foreground">
          Dieser Link ist unvollständig oder abgelaufen. Fordere bei Bedarf einen neuen Reset-Link an.
        </p>
      </AuthCardLayout>
    );
  }

  return (
    <AuthCardLayout>
      <h3 className="mb-2 flex items-center justify-center gap-2 text-lg font-bold text-primary">
        <KeyRound className="size-5" aria-hidden="true" /> Neues Passwort vergeben
      </h3>
      <p className="mb-6 text-sm leading-[1.4] text-muted-foreground">
        Der Link ist eine Stunde gültig. Verwende mindestens 12 Zeichen.
      </p>

      <form onSubmit={handleSubmit}>
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="reset-pass">Neues Passwort</FieldLabel>
            <Input
              id="reset-pass"
              type="password"
              required
              minLength={12}
              autoComplete="new-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </Field>

          <Field>
            <FieldLabel htmlFor="reset-pass-confirm">Passwort bestätigen</FieldLabel>
            <Input
              id="reset-pass-confirm"
              type="password"
              required
              minLength={12}
              autoComplete="new-password"
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
            />
          </Field>

          <Button type="submit" className="w-full hover:bg-primary-dark" disabled={pending}>
            Passwort speichern
          </Button>
        </FieldGroup>
      </form>
    </AuthCardLayout>
  );
}
